import { useState } from "react";

import { ResultsStep } from "./components/ResultsStep";
import { UploadStep } from "./components/UploadStep";
import styles from "./NetsuiteContactsPage.module.css";

import { ROUTES } from "@/config/routes";
import { triggerDownload } from "@/hooks/download";
import { useTopbarBack } from "@/hooks/useTopbarBack";
import { authService } from "@/services/authService";
import { toolsService, type NetsuiteTransformResult } from "@/services/toolsService";
import { ApiError } from "@/types/api";

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

type Status =
  | { kind: "idle" }
  | { kind: "working" }
  | { kind: "error"; message: string }
  | { kind: "done"; result: NetsuiteTransformResult };

/**
 * NetSuite Contacts Import: upload an .xlsx export, preview the transform
 * statistics, then download the email and SMS CSVs for import.
 */
export function NetsuiteContactsPage() {
  const [file, setFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  useTopbarBack({ to: ROUTES.tools, label: "Back to tools" });

  function selectFile(next: File) {
    setUploadError(null);
    const lower = next.name.toLowerCase();
    if (!lower.endsWith(".xlsx") && !lower.endsWith(".xls")) {
      setUploadError("Invalid file type. Please select an Excel (.xlsx or .xls) file.");
      return;
    }
    if (next.size > MAX_FILE_SIZE_BYTES) {
      setUploadError("File is too large. Maximum allowed size is 10 MB.");
      return;
    }
    setFile(next);
  }

  async function transform() {
    if (!file) return;
    setStatus({ kind: "working" });

    try {
      const csrfToken = await authService.getCsrfToken();
      if (!csrfToken) {
        setStatus({
          kind: "error",
          message: "Your session has expired. Refresh the page and try again.",
        });
        return;
      }

      const result = await toolsService.transformNetsuiteContacts({ file, csrfToken });
      setStatus({ kind: "done", result });
    } catch (err) {
      setStatus({ kind: "error", message: toErrorMessage(err) });
    }
  }

  function downloadEmail() {
    if (status.kind !== "done") return;
    const { emailCsv, emailFilename } = status.result;
    triggerDownload(new Blob([emailCsv], { type: "text/csv;charset=utf-8" }), emailFilename);
  }

  function downloadSms() {
    if (status.kind !== "done") return;
    const { smsCsv, smsFilename } = status.result;
    triggerDownload(new Blob([smsCsv], { type: "text/csv;charset=utf-8" }), smsFilename);
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>NetSuite Contacts Import</h1>
      </header>

      {status.kind === "done" ? (
        <ResultsStep
          result={status.result}
          onDownloadEmail={downloadEmail}
          onDownloadSms={downloadSms}
          onReset={() => {
            setFile(null);
            setUploadError(null);
            setStatus({ kind: "idle" });
          }}
        />
      ) : (
        <UploadStep
          file={file}
          error={uploadError}
          isWorking={status.kind === "working"}
          statusError={status.kind === "error" ? status.message : null}
          onSelectFile={selectFile}
          onTransform={transform}
        />
      )}
    </div>
  );
}

function toErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === "network_error") {
      return "Network error. Check your connection and try again.";
    }
    return err.message;
  }
  return "Something went wrong. Please try again.";
}
